export const OVERLAY_SAFE_TOP_OFFSET = 45
export const OVERLAY_VIEWPORT_MARGIN = 10

export interface OverlayDragPosition {
  x: number
  y: number
}

type PopperOffset = [number, number]

const getOverlayViewportPadding = () => ({
  top: OVERLAY_SAFE_TOP_OFFSET,
  right: OVERLAY_VIEWPORT_MARGIN,
  bottom: OVERLAY_VIEWPORT_MARGIN,
  left: OVERLAY_VIEWPORT_MARGIN,
})

interface ClampOverlayDragPositionParams {
  rect: DOMRect | null
  currentPosition: OverlayDragPosition
  nextPosition: OverlayDragPosition
  viewportWindow?: Window | null
  bottomInset?: number
}

export const clampOverlayDragPosition = ({
  rect,
  currentPosition,
  nextPosition,
  viewportWindow,
  bottomInset = 0,
}: ClampOverlayDragPositionParams): OverlayDragPosition => {
  const activeWindow = viewportWindow ?? (typeof window === "undefined" ? null : window)

  if (!rect || !activeWindow) {
    return nextPosition
  }

  const deltaX = nextPosition.x - currentPosition.x
  const deltaY = nextPosition.y - currentPosition.y

  const minLeft = OVERLAY_VIEWPORT_MARGIN
  const maxRight = activeWindow.innerWidth - OVERLAY_VIEWPORT_MARGIN
  const minTop = OVERLAY_SAFE_TOP_OFFSET

  let clampedX = nextPosition.x
  let clampedY = nextPosition.y

  const nextLeft = rect.left + deltaX
  const nextRight = rect.right + deltaX
  const nextTop = rect.top + deltaY
  const nextBottom = rect.bottom + deltaY

  if (nextLeft < minLeft) {
    clampedX += minLeft - nextLeft
  } else if (nextRight > maxRight) {
    clampedX -= nextRight - maxRight
  }

  if (nextTop < minTop) {
    clampedY += minTop - nextTop
  } else if (bottomInset > 0 && nextBottom > activeWindow.innerHeight - bottomInset) {
    clampedY -= Math.min(nextBottom - activeWindow.innerHeight + bottomInset, nextTop - minTop)
  }

  return { x: clampedX, y: clampedY }
}

export const getOverlayInitialPosition = (initialPosition?: Partial<OverlayDragPosition>): OverlayDragPosition => ({
  x: Math.max(initialPosition?.x ?? 0, OVERLAY_VIEWPORT_MARGIN),
  y: Math.max(initialPosition?.y ?? 0, OVERLAY_SAFE_TOP_OFFSET),
})

export const buildSafeAreaPopperModifiers = (offset: PopperOffset) => [
  {
    name: "offset",
    options: {
      offset,
    },
  },
  {
    name: "preventOverflow",
    options: {
      padding: getOverlayViewportPadding(),
      altAxis: true,
    },
  },
  {
    name: "flip",
    options: {
      padding: getOverlayViewportPadding(),
    },
  },
]
